/** Motivos de pieza Courier (valor estable; etiqueta vía i18n). */
export const COURIER_MOTIVO_OPTIONS = [
  "sample",
  "swatch",
  "color",
  "card",
  "other",
];

export function courierMotivoI18nKey(value = "") {
  const v = String(value || "")
    .trim()
    .toLowerCase();
  if (COURIER_MOTIVO_OPTIONS.includes(v)) {
    return `courierControl.reason${v.charAt(0).toUpperCase()}${v.slice(1)}`;
  }
  return null;
}

/** Compañías courier habituales para el select de Courier Control. */
export const COURIER_COMPANIES = [
  "DHL",
  "UPS",
  "FedEx",
  "TNT",
  "SF Express",
  "Aramex",
  "Other",
];

/**
 * Tipos de embalaje del Excel Couriers_Control → Parameters → Packing_Type.
 * Valor = texto del Excel (se guarda en TipoEmbalaje). Orden alfabético.
 */
export const COURIER_PACKING_OPTIONS = [
  "Box",
  "Box 1/2",
  "Box 2/2",
  "En Mano",
  "Envelope",
  "Fedex_Medium",
  "Fedex_Small",
  "MALETA",
  "TNT_Bag_1",
  "TNT_Bag_2",
];
const PACKING_I18N_KEYS = {
  Envelope: "packingEnvelope",
  Fedex_Small: "packingFedexSmall",
  Fedex_Medium: "packingFedexMedium",
  TNT_Bag_1: "packingTntBag1",
  TNT_Bag_2: "packingTntBag2",
  Box: "packingBox",
  "Box 1/2": "packingBoxHalf1",
  "Box 2/2": "packingBoxHalf2",
  MALETA: "packingSuitcase",
  "En Mano": "packingByHand",
};

export function courierPackingI18nKey(value = "") {
  const raw = String(value || "").trim();
  if (PACKING_I18N_KEYS[raw]) {
    return `courierControl.${PACKING_I18N_KEYS[raw]}`;
  }
  const lower = raw.toLowerCase();
  if (lower === "box" || lower === "envelope") {
    const canon = COURIER_PACKING_OPTIONS.find(
      (o) => o.toLowerCase() === lower,
    );
    if (canon) return `courierControl.${PACKING_I18N_KEYS[canon]}`;
  }
  return null;
}
export const ORIGIN_SDS = "SDS";
export const ORIGIN_CHINA_SUPPLIER = "China / Supplier";
export const STAGE_CHINA_ORIGIN = "ZHEJIANG C-STAGE IMP & EXP CO., LTD";

export function parseProveedoresPermitidos(raw) {
  if (!raw) return [];
  if (Array.isArray(raw))
    return raw.map((x) => String(x).trim()).filter(Boolean);
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.map((x) => String(x).trim()).filter(Boolean)
      : [];
  } catch {
    return [];
  }
}

/** Usuario de fábrica (China etc.): tiene ProveedoresPermitidos. */
export function isChinaCourierUser(user = {}) {
  return parseProveedoresPermitidos(user.ProveedoresPermitidos).length > 0;
}

function normProv(value = "") {
  return String(value).trim().toLowerCase().replace(/\s+/g, " ");
}

/** Stage China (Zhejiang C-Stage): origen fijo. */
export function isStageChinaUser(user = {}) {
  const allowed = parseProveedoresPermitidos(user.ProveedoresPermitidos).map(
    normProv,
  );
  if (!allowed.length) return false;
  return allowed.some(
    (a) =>
      a === normProv(STAGE_CHINA_ORIGIN) ||
      a.includes("c-stage") ||
      a === "121",
  );
}

/** Tipos de destino en el diálogo: China sin Proveedor. */
export function destinationTiposForCourierUser(user = {}) {
  if (isChinaCourierUser(user)) {
    return ["INTERNO", "CLIENTE"];
  }
  return ["CLIENTE", "PROVEEDOR", "INTERNO"];
}

/**
 * Opciones de origen según usuario:
 * - Stage China → solo Zhejiang C-Stage
 * - China (otros) → sus proveedores permitidos
 * - España → SDS + China genérico + todos los proveedores
 */
export function buildOriginOptionsForUser(proveedores = [], user = {}) {
  const all = Array.isArray(proveedores) ? proveedores : [];
  if (isStageChinaUser(user)) {
    return [STAGE_CHINA_ORIGIN];
  }
  if (isChinaCourierUser(user)) {
    const allowed = new Set(
      parseProveedoresPermitidos(user.ProveedoresPermitidos).map(normProv),
    );
    const names = all
      .map((p) => String(p?.N_PROVEEDOR || "").trim())
      .filter(Boolean)
      .filter(
        (name) =>
          allowed.has(normProv(name)) ||
          allowed.has(String(all.find((p) => p.N_PROVEEDOR === name)?.CODPROV)),
      );
    // también si el permitido es el nombre exacto
    const fromAllowed = parseProveedoresPermitidos(user.ProveedoresPermitidos);
    const merged = [...new Set([...names, ...fromAllowed])].filter(Boolean);
    return merged.length ? merged : fromAllowed;
  }
  return buildOriginOptions(all);
}

/** Usuarios con ProveedoresPermitidos (fábrica China/Turquía…). */
export function filterChinaUsers(users = []) {
  return (Array.isArray(users) ? users : []).filter(
    (u) =>
      String(u.ACTIVO || "S").toUpperCase() !== "N" && isChinaCourierUser(u),
  );
}

function isActiveUser(user = {}) {
  return String(user.ACTIVO || "S").toUpperCase() !== "N";
}

function proveedorTokensForUser(user = {}, proveedores = []) {
  const allowed = parseProveedoresPermitidos(user.ProveedoresPermitidos);
  const tokens = new Set(allowed.map(normProv).filter(Boolean));
  for (const raw of allowed) {
    const t = normProv(raw);
    const match = (Array.isArray(proveedores) ? proveedores : []).find((p) => {
      const name = normProv(p?.N_PROVEEDOR);
      const code = String(p?.CODPROV ?? "").trim();
      return (
        t === name ||
        t === code ||
        (name && (name.includes(t) || t.includes(name)))
      );
    });
    if (match) {
      if (match.N_PROVEEDOR) tokens.add(normProv(match.N_PROVEEDOR));
      if (match.CODPROV != null) tokens.add(String(match.CODPROV).trim());
      if (match.PAIS) tokens.add(normProv(match.PAIS));
    }
  }
  return [...tokens];
}

/**
 * Región para Solicitado por: china | turkey | spain.
 * Fábrica sin pista clara → factory (solo colegas con proveedores).
 */
export function courierRequestedByRegion(user = {}, proveedores = []) {
  if (!isChinaCourierUser(user)) return "spain";
  const text = proveedorTokensForUser(user, proveedores).join(" ");
  if (/turkey|turqu[ií]a|\btur\b|tekstil|istanbul|izmir/.test(text)) {
    return "turkey";
  }
  if (
    /china|zhejiang|c-stage|suzhou|guangzhou|shanghai|hangzhou|ningbo|beijing|121/.test(
      text,
    )
  ) {
    return "china";
  }
  return "factory";
}

/**
 * Solicitado por:
 * - China → solo usuarios China
 * - Turquía → solo usuarios Turquía
 * - España (sin ProveedoresPermitidos) → todos los activos
 * Nunca cae al listado completo si eres fábrica.
 */
export function filterRequestedByUsers(
  users = [],
  currentUser = {},
  proveedores = [],
) {
  const list = Array.isArray(users) ? users : [];
  const active = list.filter(isActiveUser);
  const myRegion = courierRequestedByRegion(currentUser, proveedores);

  if (myRegion === "spain") return active;

  let pool = active.filter((u) => {
    const r = courierRequestedByRegion(u, proveedores);
    if (myRegion === "factory") {
      if (r === "spain") return false;
      const mine = proveedorTokensForUser(currentUser, proveedores);
      const theirs = proveedorTokensForUser(u, proveedores);
      return mine.some((m) =>
        theirs.some((t) => t === m || t.includes(m) || m.includes(t)),
      );
    }
    return r === myRegion;
  });

  const currentId = currentUser?.id;
  if (
    currentId != null &&
    isActiveUser(currentUser) &&
    !pool.some((u) => Number(u.id) === Number(currentId))
  ) {
    pool = [currentUser, ...pool];
  }
  if (
    !pool.length &&
    isActiveUser(currentUser) &&
    isChinaCourierUser(currentUser)
  ) {
    return [currentUser];
  }
  return pool;
}

/**
 * Opciones de origen: SDS + China genérico + proveedores (fábricas).
 */
export function buildOriginOptions(proveedores = []) {
  const names = (Array.isArray(proveedores) ? proveedores : [])
    .map((p) => String(p?.N_PROVEEDOR || "").trim())
    .filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const label of [ORIGIN_SDS, ORIGIN_CHINA_SUPPLIER, ...names]) {
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
}

/**
 * País a partir del origen (China / SDS / PAIS del proveedor).
 */
export function inferCountryFromOrigin(origen, proveedores = []) {
  const o = String(origen || "").trim();
  if (!o) return "";
  if (/china/i.test(o)) return "China";
  if (/^sds$/i.test(o) || /sitges/i.test(o) || /spain/i.test(o)) {
    return "Spain";
  }
  const match = (Array.isArray(proveedores) ? proveedores : []).find(
    (p) =>
      String(p?.N_PROVEEDOR || "")
        .trim()
        .toLowerCase() === o.toLowerCase(),
  );
  const pais = String(match?.PAIS || "").trim();
  if (pais) return pais;
  if (/turkey|turqu[ií]a/i.test(o)) return "Turkey";
  return "";
}

/**
 * Etiqueta de destino a guardar en Courier.Destino desde el form Samples.
 */
export function destinoLabelFromEnvioDestino(
  dest = {},
  { customers = [], proveedores = [] } = {},
) {
  const tipo = String(dest.TIPO_DESTINO || "").toUpperCase();
  if (tipo === "INTERNO") return "SDS Spain";
  if (tipo === "CLIENTE") {
    const c = customers.find(
      (row) => Number(row.id) === Number(dest.ID_CLIENTE),
    );
    return String(c?.name || c?.Name || "").trim() || "";
  }
  if (tipo === "PROVEEDOR") {
    const p = proveedores.find(
      (row) => Number(row.CODPROV) === Number(dest.CODPROV),
    );
    return String(p?.N_PROVEEDOR || "").trim() || "";
  }
  return "";
}

/**
 * Intenta reconstruir el form de destino Samples desde Destino/Contacto guardados.
 */
export function inferEnvioDestinoFromCourier(
  envio = {},
  { customers = [], proveedores = [] } = {},
) {
  const destino = String(envio.Destino || "").trim();
  const contacto = String(envio.Contacto || "").trim();
  const base = {
    TIPO_DESTINO: "",
    ID_CLIENTE: null,
    CODPROV: null,
    ID_CONTACTO: null,
    PERSONA_ENVIO: contacto,
    ID_Marca: null,
  };
  if (!destino) return base;

  const lower = destino.toLowerCase();
  if (
    lower === "sds" ||
    lower.includes("sds") ||
    lower.includes("interno") ||
    lower.includes("internal")
  ) {
    return { ...base, TIPO_DESTINO: "INTERNO" };
  }

  const customer = customers.find(
    (c) =>
      String(c?.name || c?.Name || "")
        .trim()
        .toLowerCase() === lower,
  );
  if (customer) {
    return {
      ...base,
      TIPO_DESTINO: "CLIENTE",
      ID_CLIENTE: customer.id,
    };
  }

  const proveedor = proveedores.find(
    (p) =>
      String(p?.N_PROVEEDOR || "")
        .trim()
        .toLowerCase() === lower,
  );
  if (proveedor) {
    return {
      ...base,
      TIPO_DESTINO: "PROVEEDOR",
      CODPROV: proveedor.CODPROV,
    };
  }

  return base;
}

export function userDisplayName(user = {}) {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  return (
    String(user.name || user.username || user.email || "").trim() ||
    (user.id != null ? `#${user.id}` : "")
  );
}
